import React, { useEffect, useRef, useState } from 'react';
import { listPhotos, uploadPhoto, deletePhoto, photoUrl } from '../services/photoService';

const WorkOrderPhotos = ({ workOrderId }) => {
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [lightbox, setLightbox] = useState(null);
  const inputRef = useRef(null);

  const fetchPhotos = async () => {
    try {
      const data = await listPhotos({ contextType: 'work_order', contextId: Number(workOrderId) });
      setPhotos(data.photos || []);
    } catch {
      // Non-fatal.
    }
  };

  useEffect(() => {
    if (!workOrderId) return;
    fetchPhotos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workOrderId]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    setError('');
    try {
      for (const f of files) {
        await uploadPhoto(f, { contextType: 'work_order', contextId: Number(workOrderId) });
      }
      await fetchPhotos();
    } catch (err) {
      setError(err.response?.data?.error || 'Photo upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleDelete = async (photo) => {
    if (!window.confirm('Delete this photo?')) return;
    try {
      await deletePhoto(photo.public_id);
      setPhotos((prev) => prev.filter((p) => p.public_id !== photo.public_id));
      if (lightbox?.public_id === photo.public_id) setLightbox(null);
    } catch (err) {
      setError('Failed to delete photo');
    }
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-900">Photos ({photos.length})</h3>
          <label className={`bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded text-sm font-medium cursor-pointer ${uploading ? 'opacity-50 cursor-wait' : ''}`}>
            {uploading ? 'Uploading…' : '+ Add photo'}
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              onChange={handleUpload}
              disabled={uploading}
              className="hidden"
            />
          </label>
        </div>
        {error && (
          <div className="mb-3 text-sm text-red-600">{error}</div>
        )}
        {photos.length === 0 ? (
          <p className="text-sm text-gray-400 italic">
            No photos yet. Snap one on your phone to document the problem or the repair.
          </p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {photos.map((p) => (
              <div key={p.public_id} className="relative group aspect-square bg-gray-100 rounded overflow-hidden">
                <button
                  type="button"
                  onClick={() => setLightbox(p)}
                  className="w-full h-full block"
                >
                  <img
                    src={photoUrl(p)}
                    alt={p.caption || p.filename}
                    loading="lazy"
                    className="w-full h-full object-cover hover:opacity-90 transition"
                  />
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(p)}
                  className="absolute top-1 right-1 bg-black/60 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition"
                  title="Delete photo"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {lightbox && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 cursor-pointer"
          onClick={() => setLightbox(null)}
        >
          <img
            src={photoUrl(lightbox)}
            alt={lightbox.caption || lightbox.filename}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            type="button"
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 text-white text-3xl leading-none"
          >
            ×
          </button>
        </div>
      )}
    </>
  );
};

export default WorkOrderPhotos;
